import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { CompanyAddressChangeStepsEnum } from './companyAddressChangeStepsEnum';
import { RelationshipType, RelationshipTypeLabelsPlural } from '@models/relationships/relationship-type.enum';
import { Relationship } from '@models/relationships/relationship';
import { Address } from "../../../../models/address/address";
import { autocompleteServiceToken } from '../../../../shared/services/autocomplete.service';
import { AuxiliaryApiService } from '@shared/services/auxiliary-api.service';
import { CountriesService } from "../../../../shared/services/countries.service";
import { setControlDisabled } from "../../../../shared/utils/set-control-disabled";

import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { CompanyChangeAddress } from "../../../models/company";
import {
  addressIncludesPoBox,
  customForm484A1Errors,
  SelectOptionWithHelperText
} from "../../../models/company/company-change-address.model";
import {
  AddressAndOccupierFormGroupComponent,
  AddressAndOccupierFormGroupControls
} from "../../../reusable-form-groups/address-and-occupier-form-group/address-and-occupier-form-group.component";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";

export interface A1CompanyAddressChangeFormGroupControls extends AddressAndOccupierFormGroupControls {
  applyToRegistered: FormControl<boolean | null>;
  applyToPrincipal: FormControl<boolean | null>;
  relationshipIds: FormArray<FormControl<boolean>>;
}

export type A1CompanyAddressChangeFormGroup = FormGroup<A1CompanyAddressChangeFormGroupControls>;

@Component({
  selector: 'app-company-change-address',
  standalone: true,
  templateUrl: './company-change-address.component.html',
  styleUrls: [
    './company-change-address.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
  ],
  imports: [
    ReactiveFormsModule,
    CheckboxComponent,
    ValidationErrorComponent,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    AddressAndOccupierFormGroupComponent,
  ],
  providers: [{ provide: autocompleteServiceToken, useClass: AuxiliaryApiService }]
})
export class CompanyChangeAddressComponent extends BaseStepperFormComponent<CompanyAddressChangeStepsEnum, CompanyChangeAddress> implements OnInit {
  @Input() relationships: Record<string, Relationship[]> = {};
  @Input() officersSelectOptions: SelectOptionWithHelperText[] = [];
  @Input() registeredAddress: Address = new Address();
  @Input() principalAddress: Address = new Address();

  readonly customErrors = customForm484A1Errors;
  override readonly StepsEnum = CompanyAddressChangeStepsEnum;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;
  expandedAddressForm = false;

  form: A1CompanyAddressChangeFormGroup = new FormGroup<A1CompanyAddressChangeFormGroupControls>({
    ...AddressAndOccupierFormGroupComponent.defineFormControls(),
    applyToRegistered: new FormControl<boolean | null>(null),
    applyToPrincipal: new FormControl<boolean | null>(null),
    relationshipIds: new FormArray<FormControl<boolean>>([]),
  });

  override stepperForm = new FormGroup({
    [CompanyAddressChangeStepsEnum.FormDescription]: new FormGroup({}),
    [CompanyAddressChangeStepsEnum.NewAddress]: this.form
  });

  constructor() {
    super();
    this.setupSteps(CompanyAddressChangeStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDateOfEstablishmentMinDate(this.companyChangeData.dateOfEstablishment);
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    this.setupDefaultState();
    this.setupChange();
  }

  override buildDocument(): Document | null {
    const relationshipIds = this.form.value.relationshipIds
      ?.reduce((acc, option, index) => {
        if (option) {
          acc.push(this.relationships[this.officersSelectOptions[index].value as string].map(officer => officer.relationshipId));
        }

        return acc;
      }, [] as string[][])
      ?.flat();

    const companyChangeAddress = new CompanyChangeAddress({
      ...this.form.value as Partial<CompanyChangeAddress>,
      relationshipIds
    });

    try {
      return new Document({
        changes: [companyChangeAddress],
        entityId: this.companyChangeData?.entityId,
        type: 'c:484',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeAddress);
  }

  override setupChange(change: CompanyChangeAddress = this.formModel): void {
    if (!this.isEdit && change === this.formModel)
      return;

    this.form.patchValue({
      ...change,
      relationshipIds: []
    });
  }

  checkValidity(): void {
    this.form.updateValueAndValidity();
  }

  private setupDefaultState(): void {
    setControlDisabled(this.form.controls.occupierNameIfDoesntOccupy);
    setControlDisabled(this.form.controls.companyHaveOccupiersConsent);

    this.officersSelectOptions.forEach(() => {
      this.form.controls.relationshipIds.push(new FormControl<boolean>(false, { nonNullable: true }));
    });

    this.setupValidators();

    if (this.formModel.relationshipIds.length) {
      this.formModel.relationshipIds.forEach((relationshipId) => {
        const selectedOfficerIndex = this.officersSelectOptions
          .findIndex((officer) => this.relationships[officer.value as string]
            .some(relationship => relationship.relationshipId === relationshipId));

        this.form.controls.relationshipIds.at(selectedOfficerIndex)?.setValue(true, { emitEvent: false });
      });

      if (this.formModel.address.normalizedFullAddress) {
        this.form.controls.address.patchValue(this.formModel.address);
      }
    }
  }

  private setupValidators(): void {
    this.addSelectAtLeastOneOptionValidator();
    this.addDoNotAllowForeignAddressAsCompanyAddressesValidator();
    this.addDoNotAllowForeignAddressForAllDirectorsValidator();
    this.addCareOfResidentialAddressValidator();
    this.addCompanyPoBoxAddressValidator();
    this.addOfficeholdersPoBoxAddressValidator(RelationshipType.Director);
    this.addOfficeholdersPoBoxAddressValidator(RelationshipType.AlternativeDirector);
    this.addOfficeholdersPoBoxAddressValidator(RelationshipType.Secretary);
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);
  }

  private addSelectAtLeastOneOptionValidator(): void {
    this.form.addValidators(((form: A1CompanyAddressChangeFormGroup) => {
      // set validator that checks if one of controls' value equal to true
      const applyToRegistered = form.value.applyToRegistered;
      const applyToPrincipal = form.value.applyToPrincipal;
      const atLeastOneOfficerSelected = form.value.relationshipIds?.some((value) => value);

      if (!applyToRegistered && !applyToPrincipal && !atLeastOneOfficerSelected) {
        return { required: 'true' };
      }

      return null;
    }) as ValidatorFn);
  }

  //  do not allow:
  //  - using a foreign address as a registered address
  //  - using a foreign address as a principal address
  private addDoNotAllowForeignAddressAsCompanyAddressesValidator(): void {
    this.form.addValidators(((form: A1CompanyAddressChangeFormGroup) => {
      const applyToRegistered = !!form.value.applyToRegistered;
      const applyToPrincipal = !!form.value.applyToPrincipal;

      if (form.value.address?.country && (applyToRegistered || applyToPrincipal)) {
        const isAustraliaSelected = CountriesService.isAustralianStateOrAustralia(form.controls.address.controls.country.value);

        if (!isAustraliaSelected) {
          return { foreignAddressForCompany: 'true' };
        }
      }

      return null;
    }) as ValidatorFn);
  }

  //  do not allow:
  //  - all directors changing their addresses to a foreign address
  private addDoNotAllowForeignAddressForAllDirectorsValidator(): void {
    this.form.addValidators(((form: A1CompanyAddressChangeFormGroup) => {

      const allDirectorsSelected = form.value.relationshipIds?.reduce((acc, isSelected, index) => {
        const isDirector = this.relationships[this.officersSelectOptions[index].value as string]
          ?.some((relationship) => relationship.type === RelationshipType.Director);

        acc.push({
          isDirector,
          isSelected
        });
        return acc;
      }, [] as { isDirector: boolean, isSelected: boolean }[])
        .filter((director) => director.isDirector)
        .every(director => director.isSelected);

      const isAustraliaSelected = CountriesService.isAustralianStateOrAustralia(form.controls.address.controls.country.value);

      if (form.value.address?.country && allDirectorsSelected && !isAustraliaSelected) {
        return { foreignAddressForAllDirectors: true };
      }

      return null;
    }) as ValidatorFn);
  }

  // do not allow:
  // - Care Of for residential addresses
  private addCareOfResidentialAddressValidator(): void {
    this.form.addValidators(((form: A1CompanyAddressChangeFormGroup) => {
      const directorsSelected = !!form.value.relationshipIds?.reduce((acc, isSelected, index) => {
        const isDirector = this.relationships[this.officersSelectOptions[index].value as string]
          ?.some((relationship) => relationship.type === RelationshipType.Director);
        acc.push({
          isDirector,
          isSelected
        });
        return acc;
      }, [] as { isDirector: boolean, isSelected: boolean }[])
        .some((director) => director.isDirector && director.isSelected);

      const principalAddressSelected = !!form.value.applyToPrincipal;
      const isCareOfEntered = !!form.value.address?.careOf?.length;

      if (directorsSelected || principalAddressSelected) {
        return isCareOfEntered ? { careOfResidential: true } : null;
      }

      return null;
    }) as ValidatorFn);
  }

  // do not allow:
  // - set any company address with PO Box in it
  private addCompanyPoBoxAddressValidator(): void {
    const companyPoBoxAddressValidator = (control: AbstractControl): ValidationErrors | null => {
      const form = control as A1CompanyAddressChangeFormGroup;
      const address = new Address(form.value.address as Partial<Address>);
      const anyCompanyAddressSelected = Boolean(form.controls.applyToRegistered.value || form.controls.applyToPrincipal.value);

      return anyCompanyAddressSelected && addressIncludesPoBox(address.normalizedFullAddress)
        ? { poBoxAddress: true }
        : null;
    };
    this.form.addValidators([companyPoBoxAddressValidator]);
  }

  // do not allow:
  // - set PO Box address for [officeholders with relationshipType]
  private addOfficeholdersPoBoxAddressValidator(relationshipType = RelationshipType.Director): void {
    const noOfficersOfSelectedType = !Object.values(this.relationships)
      .flat()
      .some((officer) => officer.type === relationshipType);

    if (noOfficersOfSelectedType) return;

    const officeholderPoBoxAddressValidator = (control: AbstractControl) => {
      const form = control as A1CompanyAddressChangeFormGroup;

      // skip validator if
      // - no officeholders selected
      // - address doesn't include "PO Box"
      const areOfficeholdersSelected = (form.controls.relationshipIds.value).some((officeholderSelected) => officeholderSelected);
      const proposedAddressIncludesPoBox = addressIncludesPoBox(form.controls.address.controls.normalizedFullAddress.value ?? '') || addressIncludesPoBox(form.controls.address.controls.street.value ?? '');

      if (!areOfficeholdersSelected || !proposedAddressIncludesPoBox) {
        return null;
      }

      for (const offId of this.selectedOfficeholdersIds(form.controls.relationshipIds)) {
        const officer = this.relationships[offId].find((officer) => officer.type === relationshipType);
        if (!officer) continue;

        return { poBoxAddress: RelationshipTypeLabelsPlural[relationshipType] };
      }

      return null;
    };
    this.form.addValidators([officeholderPoBoxAddressValidator]);
  }

  private selectedOfficeholdersIds(relationshipIdsFormArray: FormArray<FormControl<boolean>>): string[] {
    return relationshipIdsFormArray.value
      .reduce((ids, selected, index) => {
        const id = this.officersSelectOptions[index].value;
        if (selected && id) ids.push(this.officersSelectOptions[index].value as string);
        return ids;
      }, [] as string[]);
  }

  get occupierPartHidden(): boolean {
    return this.form.controls.applyToRegistered.value !== true;
  }

  get isCheckboxErrorHidden(): boolean {
    return !(
      (this.form.controls.applyToRegistered.touched || this.form.controls.applyToPrincipal.touched || this.form.controls.relationshipIds.touched)
      && this.form.errors);
  }

  get isOfficeholderWasSelected(): boolean {
    return this.form.controls.relationshipIds.controls.some((control) => control.value);
  }
}

